import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { getAuthToken } from 'modules/auth/authSelectors';
import { useSelector } from 'react-redux';
import appConfigSelectors from 'modules/appConfig/selectors';
import useBrandName from 'utility/hooks/useBrandName';
import useChatMiaw from './useChatMiaw';

interface ChatMiawGateProps {
	children: React.ReactNode;
}
export interface ChatWidgetElement extends HTMLElement {
	preselectDepartment?: string;
	color?: string;
	popups?: boolean;
	brand?: string;
}
// Define which routes should use which department
const ROUTE_DEPARTMENTS: Record<string, string> = {
	'/services/firewalls': 'Sales',
};

/**
 * Gate component that initializes and manages the chat service across the application
 * @param {object} props
 * @param {React.ReactNode} props.children - The child elements to render
 * @returns {React.JSX.Element}
 */
const ChatMiawGate = ({ children }: ChatMiawGateProps) => {
	const config = useSelector(appConfigSelectors.getAppConfig);
	const chatWidgetRef = useRef<ChatWidgetElement | null>(null);
	const { chatConfig } = config || {};
	const brand = useBrandName();
	const authToken = useSelector(getAuthToken);
	const { pathname } = useLocation();

	const { ready } = useChatMiaw(chatConfig, {
		authToken,
		enabled: chatConfig?.enabled,
	});

	// Determine the department based on the current route
	const department = ROUTE_DEPARTMENTS[pathname] || '';
	useEffect(() => {
		const chatWidget = chatWidgetRef.current;

		if (!ready || !chatWidget) {
			return () => {};
		}

		if (department) {
			chatWidget.setAttribute('preselectDepartment', department);
		}

		return () => {
			if (chatWidget) {
				chatWidget.removeAttribute('preselectDepartment');
			}
		};
	}, [ready, department, pathname]);

	return (
		<>
			{children}
			{ready && (
				<chat-widget
					ref={chatWidgetRef}
					popups={false}
					color="#25A864"
					brand={brand}
				/>
			)}
		</>
	);
};

export default ChatMiawGate;
